import { useState, type CSSProperties, type ReactNode } from 'react';
import type { EbookClient } from '../services/ebooks';
import { EbookShelfCover } from './EbookShelfCover';
import './ebook-book-model.css';

/** Authorized cover imagery on a closed, interactive shelf book. */
export function EbookBookModel({
  client,
  path,
  title,
  variant,
  decoration,
}: {
  client: EbookClient;
  path: string | null;
  title: string;
  variant: 'shelf';
  decoration?: ReactNode;
}) {
  const [binding, setBinding] = useState<{ path: string | null; color: string } | null>(null);
  const sampleBinding = (image: HTMLImageElement) => {
    // Sample the actual authorized cover once. No extra image request, and no
    // book-specific palettes or author/publisher information are fabricated.
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext('2d');
    if (!context) return;
    try {
      context.drawImage(
        image,
        0,
        0,
        image.naturalWidth * 0.06,
        image.naturalHeight * 0.35,
        0,
        0,
        1,
        1,
      );
      const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
      setBinding({ path, color: `rgb(${r} ${g} ${b})` });
    } catch {
      // A cover can still be read if its color cannot be sampled.
    }
  };
  return (
    <div
      className={`ebook-book-model ebook-book-model--${variant}`}
      style={
        { '--binding-color': binding?.path === path ? binding.color : '#d9d2c1' } as CSSProperties
      }
    >
      <>
        <div className="ebook-book-model__shadow" aria-hidden="true" />
        <div className="ebook-book-model__camera">
          <div className="ebook-book-model__body">
            <div className="ebook-book-model__back" aria-hidden="true" />
            <div
              className="ebook-book-model__pages ebook-book-model__pages--fore"
              aria-hidden="true"
            />
            <div
              className="ebook-book-model__pages ebook-book-model__pages--top"
              aria-hidden="true"
            />
            <div
              className="ebook-book-model__pages ebook-book-model__pages--bottom"
              aria-hidden="true"
            />
            <div className="ebook-book-model__spine" aria-hidden="true">
              <span>{title.replace(/[《》]/g, '')}</span>
            </div>
            <div className="ebook-book-model__front">
              <EbookShelfCover
                client={client}
                path={path}
                title={title}
                onImageLoad={sampleBinding}
              />
              {decoration}
            </div>
          </div>
        </div>
      </>
    </div>
  );
}
